/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Timeline context
import { useTimeline } from "e4acomponents/Timeline/context";

// Custom styles for the TimelineItem
import timelineItem from "e4acomponents/Timeline/TimelineItem/styles";

function TimelineItem({ color, icon, title, dateTime, comment, description, lastItem, onOpenSnapshot }) {
  const isDark = useTimeline();

  return (
    <MDBox display="flex" justifyContent="space-between" position="relative">
    <MDBox position="relative" mb={3} sx={(theme) => timelineItem(theme, { lastItem, isDark })}>
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        bgColor={color}
        color="white"
        width="2rem"
        height="2rem"
        borderRadius="50%"
        position="absolute"
        top="8%"
        left="2px"
        zIndex={2}
        sx={{ fontSize: ({ typography: { size } }) => size.sm }}
      >
        <Icon fontSize="inherit">{icon}</Icon>
      </MDBox>
      <MDBox ml={5.75} pt={description ? 0.7 : 0.5} lineHeight={0} maxWidth="30rem">
        <MDTypography variant="button" fontWeight="medium" color={isDark ? "white" : "dark"}>
          {title}
        </MDTypography>
        <MDBox mt={0.5}>
          <MDTypography variant="caption" color={isDark ? "secondary" : "text"}>
            {dateTime}
          </MDTypography>
        </MDBox>
        <MDBox mt={2}>
          {description ? (
            <MDTypography variant="button" color={isDark ? "white" : "dark"}>
              <strong>Description:</strong> {description}
            </MDTypography>
          ) : null}
        </MDBox>
        {comment ? (
          <MDBox mt={0.5} mb={1.5}>
            <MDTypography variant="button" color={isDark ? "white" : "dark"}>
              <strong>Comment:</strong> {comment}
            </MDTypography>
          </MDBox>
        ) : null}
      </MDBox>
    </MDBox>
          {onOpenSnapshot && (
        <MDBox ml={5.75} pt={description ? 0.7 : 0.5} lineHeight={0} maxWidth="30rem">
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={onOpenSnapshot}
          >
            View Old Revision
          </MDButton>
        </MDBox>
      )}
    </MDBox>
  );
}

// Setting default values for the props of TimelineItem
TimelineItem.defaultProps = {
  color: "info",
  lastItem: false,
  description: "",
};

// Typechecking props for the TimelineItem
TimelineItem.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  dateTime: PropTypes.string.isRequired,
  description: PropTypes.string,
  lastItem: PropTypes.bool,
};

export default TimelineItem;
